/**
 * ContractDetailPanel - Panel detaliat al contractului cu anexe
 * Se deschide când se dă click pe un contract din DocumentsTab
 * Conține: info contract, deviz, și generatoare de anexe
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ArrowLeft, FileText, ClipboardList, FileSignature, CheckCircle2,
  Plus, Eye, Download, Trash2, Edit, MapPin, Building, Calendar,
  Receipt, Loader2, Printer, ReceiptText, Mail, HardHat, ShieldCheck,
  ExternalLink, RefreshCw, AlertTriangle, Upload
} from "lucide-react";
import { safeOpenHTMLWindow } from "@/utils/safeHTML";
import { downloadHtmlAsPdf } from "@/utils/htmlToPdf";
import SituatieLucrariEditor from "./SituatieLucrariEditor";
import PVPreluareGenerator from "./PVPreluareGenerator";
import PVReceptieGenerator from "./PVReceptieGenerator";
import ConventieSSMGenerator from "./ConventieSSMGenerator";
import AnexaGDPRGenerator from "./AnexaGDPRGenerator";
import SendContractDialog from "./SendContractDialog";

import ContractEditor from "./ContractEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ANNEX_TYPES = [
  {
    type: "pv_preluare",
    label: "PV Preluare",
    title: "Amplasament",
    icon: MapPin,
    color: "emerald",
    description: "Proces verbal de predare-primire amplasament",
  },
  {
    type: "situatie_lucrari",
    label: "Situații de Lucrări",
    title: "(multiple anexe individuale)",
    icon: ClipboardList,
    color: "blue",
    description: "Situații de lucrări — fiecare devine o anexă cu numerotare proprie",
  },
  {
    type: "pv_receptie",
    label: "PV Recepție",
    title: "Finală",
    icon: CheckCircle2,
    color: "violet",
    description: "Proces verbal de recepție la terminarea lucrărilor",
  },
  {
    type: "conventie_ssm",
    label: "Convenție SSM",
    title: "și SU (PSI)",
    icon: HardHat,
    color: "amber",
    description: "Convenție de securitate și sănătate în muncă + situații de urgență, semnată de Beneficiar și Executant",
  },
  {
    type: "anexa_gdpr",
    label: "Acord GDPR",
    title: "Prelucrare date personale",
    icon: ShieldCheck,
    color: "indigo",
    description: "Acord GDPR (Reg. UE 679/2016 + Legea 190/2018) — pentru colectarea legală a datelor Beneficiarului (nume, CNP/CUI, adresă)",
  },
];

const colorMap = {
  emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", badge: "bg-emerald-100 text-emerald-700", btn: "bg-emerald-600 hover:bg-emerald-700", leftBorder: "border-l-emerald-500" },
  blue: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", badge: "bg-blue-100 text-blue-700", btn: "bg-blue-600 hover:bg-blue-700", leftBorder: "border-l-blue-500" },
  violet: { bg: "bg-violet-50", border: "border-violet-200", text: "text-violet-700", badge: "bg-violet-100 text-violet-700", btn: "bg-violet-600 hover:bg-violet-700", leftBorder: "border-l-violet-500" },
  amber: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", badge: "bg-amber-100 text-amber-700", btn: "bg-amber-600 hover:bg-amber-700", leftBorder: "border-l-amber-500" },
  indigo: { bg: "bg-indigo-50", border: "border-indigo-200", text: "text-indigo-700", badge: "bg-indigo-100 text-indigo-700", btn: "bg-indigo-600 hover:bg-indigo-700", leftBorder: "border-l-indigo-500" },
};

export default function ContractDetailPanel({ contract, companyId, onBack, onRefresh, onNavigateToDeviz, autoOpenSend = false }) {
  const [annexes, setAnnexes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [devizData, setDevizData] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null); // {in_sync, diffs, ...}
  const [syncing, setSyncing] = useState(false);
  const [activeEditor, setActiveEditor] = useState(null); // { type, annex? }
  const [editingAnnex, setEditingAnnex] = useState(null);
  const [invoiceCache, setInvoiceCache] = useState({}); // { invoiceId: invoiceData }
  const [showInvoiceDetail, setShowInvoiceDetail] = useState(null); // invoice object
  const [showContractEditor, setShowContractEditor] = useState(false);
  const [showSendContract, setShowSendContract] = useState(false);
  const [companyData, setCompanyData] = useState(null);
  const [emailHistory, setEmailHistory] = useState([]);

  // Upload external invoice (linked to an SL annex)
  const [uploadSLDialog, setUploadSLDialog] = useState(null); // { annex } | null
  const [uploadSLForm, setUploadSLForm] = useState({
    file: null,
    invoice_number: "",
    invoice_date: new Date().toISOString().slice(0, 10),
    total_amount: "",
    supplier_cui: "",
    notes: "",
  });
  const [uploadSLParsing, setUploadSLParsing] = useState(false);
  const [uploadSLSubmitting, setUploadSLSubmitting] = useState(false);

  const contractData = (() => {
    if (!contract?.contract_data) return {};
    if (typeof contract.contract_data === "string") {
      try { return JSON.parse(contract.contract_data); } catch (error) { return {}; }
    }
    return contract.contract_data;
  })();

  const fetchAnnexes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/contracts/${contract.id}/annexes?company_id=${companyId}`);
      const annexList = res.data || [];
      setAnnexes(annexList);

      // Fetch invoice details for SLs that have invoice_id
      const slWithInvoice = annexList.filter(a => a.annex_type === "situatie_lucrari" && a.invoice_id);
      if (slWithInvoice.length > 0) {
        const cache = { ...invoiceCache };
        await Promise.all(slWithInvoice.map(async (a) => {
          if (!cache[a.invoice_id]) {
            try {
              const invRes = await axios.get(`${API}/invoices/${a.invoice_id}?company_id=${companyId}`);
              cache[a.invoice_id] = invRes.data;
            } catch (error) { console.error('Failed to fetch invoice for contract:', error); }
          }
        }));
        setInvoiceCache(cache);
      }
    } catch (err) {
      console.error("Error fetching annexes:", err);
    } finally {
      setLoading(false);
    }
  }, [contract.id, companyId]);

  const fetchDeviz = useCallback(async () => {
    const devizId = contractData.deviz_id;
    if (!devizId) return;
    try {
      const res = await axios.get(`${API}/devize/${devizId}?company_id=${companyId}`);
      setDevizData(res.data);
    } catch (err) {
      console.error("Error fetching deviz:", err);
    }
  }, [contractData.deviz_id, companyId]);

  const fetchSyncStatus = useCallback(async () => {
    if (!contractData.deviz_id) { setSyncStatus(null); return; }
    try {
      const res = await axios.get(`${API}/contracts/${contract.id}/deviz-sync-status?company_id=${companyId}`);
      setSyncStatus(res.data);
    } catch (err) {
      console.error("Error fetching sync status:", err);
    }
  }, [contract.id, contractData.deviz_id, companyId]);

  const handleSyncDeviz = async () => {
    try {
      setSyncing(true);
      const res = await axios.post(`${API}/contracts/${contract.id}/sync-from-deviz?company_id=${companyId}`);
      if (res.data.updated) {
        toast.success(res.data.message);
        if (onRefresh) onRefresh();
        else await fetchSyncStatus();
      } else {
        toast.info(res.data.message);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nu am putut sincroniza contractul");
    } finally {
      setSyncing(false);
    }
  };

  const fetchEmailHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/contracts/${contract.id}/email-history`);
      setEmailHistory(res.data || []);
    } catch (err) {
      console.error("Error fetching email history:", err);
    }
  }, [contract.id]);

  useEffect(() => {
    fetchAnnexes();
    fetchDeviz();
    fetchEmailHistory();
    fetchSyncStatus();
  }, [fetchAnnexes, fetchDeviz, fetchEmailHistory, fetchSyncStatus]);

  useEffect(() => {
    if (!companyId) return;
    axios.get(`${API}/companies/${companyId}`)
      .then((res) => setCompanyData(res.data))
      .catch((err) => console.warn("Nu s-au putut prelua datele companiei:", err));
  }, [companyId]);

  useEffect(() => {
    if (autoOpenSend) setShowSendContract(true);
  }, [autoOpenSend]);

  const handleCreateAnnex = async (type) => {
    setActiveEditor({ type, annex: null });
  };

  const handleEditAnnex = (annex) => {
    setEditingAnnex(annex);
    setActiveEditor({ type: annex.annex_type, annex });
  };

  const handleSaveAnnex = async (annexData) => {
    try {
      if (editingAnnex) {
        await axios.put(`${API}/contracts/${contract.id}/annexes/${editingAnnex.id}`, annexData);
        toast.success("Anexă actualizată!");
      } else {
        await axios.post(`${API}/contracts/${contract.id}/annexes`, {
          ...annexData,
          company_id: companyId,
        });
        toast.success("Anexă creată!");
      }
      setActiveEditor(null);
      setEditingAnnex(null);
      fetchAnnexes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare la salvarea anexei");
    }
  };

  const handleDeleteAnnex = async (annexId) => {
    if (!confirm("Sigur doriți să ștergeți această anexă?")) return;
    try {
      await axios.delete(`${API}/contracts/${contract.id}/annexes/${annexId}`);
      toast.success("Anexă ștearsă!");
      fetchAnnexes();
    } catch (err) {
      toast.error("Eroare la ștergere");
    }
  };

  const viewAnnexHTML = (annex) => {
    if (annex.html_content) {
      safeOpenHTMLWindow(annex.html_content);
    } else {
      toast.info("Anexa nu are conținut HTML generat");
    }
  };

  const printAnnex = (annex) => {
    if (!annex.html_content) {
      toast.info("Anexa nu are conținut generat");
      return;
    }
    // Deschidem anexa într-un tab nou și printăm ACEL tab.
    // (printarea dintr-un iframe ascuns printa pagina aplicației pe Chrome/Android)
    const w = safeOpenHTMLWindow(annex.html_content);
    if (!w) {
      toast.error("Browser-ul a blocat fereastra. Permite pop-up-uri și reîncearcă.");
      return;
    }
    let printed = false;
    const doPrint = () => {
      if (printed) return;
      printed = true;
      try {
        w.focus();
        w.print();
      } catch (err) {
        console.warn("Print window error:", err);
      }
    };
    w.addEventListener("load", () => setTimeout(doPrint, 400));
    setTimeout(doPrint, 1500); // fallback dacă 'load' a trecut deja
  };

  const downloadAnnexPDF = async (annex) => {
    if (!annex.html_content) {
      toast.info("Anexa nu are conținut HTML generat");
      return;
    }
    toast.loading("Se generează PDF-ul...", { id: "pdf-gen" });

    const ctrNo = (annex.data && annex.data.contract_number) || contractData?.contract_number || "";
    const ctrSuffix = ctrNo ? ` CTR ${ctrNo}` : "";
    const filename = `${annex.annex_label}${ctrSuffix} - ${annex.title}`;

    try {
      await downloadHtmlAsPdf(annex.html_content, filename);
      toast.success("PDF descărcat!", { id: "pdf-gen" });
    } catch (err) {
      console.error("PDF generation error:", err);
      toast.error("Eroare la generarea PDF-ului", { id: "pdf-gen" });
    }
  };

  const handleGenerateInvoiceFromSL = async (annex) => {
    if (annex.invoice_id) {
      toast.info("Această situație de lucrări are deja factură generată");
      return;
    }
    toast.loading("Se generează factura...", { id: "inv-sl" });
    try {
      const res = await axios.post(`${API}/invoices/from-situatie/${annex.id}?company_id=${companyId}`);
      if (res.data.is_new) {
        const inv = res.data.invoice || res.data;
        toast.success(`Factură creată: ${inv.series || ""}${inv.number || ""}`, { id: "inv-sl" });
        // Cache the new invoice
        if (inv.id || res.data.invoice_id) {
          const invId = inv.id || res.data.invoice_id;
          setInvoiceCache(prev => ({ ...prev, [invId]: inv }));
        }
      } else {
        toast.info("Situația de lucrări are deja factură", { id: "inv-sl" });
      }
      fetchAnnexes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare la generarea facturii", { id: "inv-sl" });
    }
  };

  const openUploadSLDialog = (annex) => {
    setUploadSLForm({
      file: null,
      invoice_number: "",
      invoice_date: new Date().toISOString().slice(0, 10),
      total_amount: "",
      supplier_cui: "",
      notes: "",
    });
    setUploadSLDialog({ annex });
  };

  const handlePickPdfForSL = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Doar fișiere PDF sunt acceptate");
      return;
    }
    setUploadSLForm((f) => ({ ...f, file }));
    // Try auto-extract fields
    setUploadSLParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/invoices/parse-external-pdf`, fd, {
        params: { company_id: companyId },
        headers: { "Content-Type": "multipart/form-data" },
      });
      const ex = res.data?.extracted || {};
      setUploadSLForm((f) => ({
        ...f,
        invoice_number: ex.invoice_number || f.invoice_number,
        invoice_date: ex.invoice_date || f.invoice_date,
        total_amount: ex.total_amount != null ? String(ex.total_amount) : f.total_amount,
        supplier_cui: ex.supplier_cui || f.supplier_cui,
      }));
      const detected = [
        ex.invoice_number && "număr",
        ex.invoice_date && "dată",
        ex.total_amount != null && "sumă",
        ex.supplier_cui && "CUI",
      ].filter(Boolean);
      if (detected.length) {
        toast.success(`Date extrase: ${detected.join(", ")}`);
      } else {
        toast.info("Nu s-au putut extrage datele automat — completează manual.");
      }
    } catch (err) {
      toast.warning("Extragerea automată a eșuat — completează manual.");
    } finally {
      setUploadSLParsing(false);
    }
  };

  const handleSubmitUploadSL = async () => {
    if (!uploadSLDialog?.annex) return;
    const { file, invoice_number, invoice_date, total_amount, supplier_cui, notes } = uploadSLForm;
    if (!file) { toast.error("Selectează un PDF"); return; }
    if (!invoice_number.trim()) { toast.error("Introdu numărul facturii"); return; }
    if (!invoice_date) { toast.error("Introdu data facturii"); return; }
    const amount = parseFloat(total_amount);
    if (!Number.isFinite(amount) || amount <= 0) { toast.error("Introdu o sumă totală validă"); return; }

    try {
      setUploadSLSubmitting(true);
      const fd = new FormData();
      fd.append("file", file);
      fd.append("invoice_number", invoice_number.trim());
      fd.append("invoice_date", invoice_date);
      fd.append("total_amount", String(amount));
      if (supplier_cui) fd.append("supplier_cui", supplier_cui.trim());
      if (notes) fd.append("notes", notes);
      const res = await axios.post(
        `${API}/invoices/upload-external-for-sl/${uploadSLDialog.annex.id}`,
        fd,
        {
          params: { company_id: companyId },
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      toast.success("Factură încărcată și legată de Situația de Lucrări");
      const inv = res.data?.invoice;
      if (inv?.id) {
        setInvoiceCache((prev) => ({ ...prev, [inv.id]: inv }));
      }
      setUploadSLDialog(null);
      fetchAnnexes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nu am putut încărca factura");
    } finally {
      setUploadSLSubmitting(false);
    }
  };

  // If an editor is active, show it
  if (activeEditor) {
    const editorProps = {
      contractData: { ...contractData, contract_id: contract.id },
      devizData,
      companyId,
      existingAnnex: activeEditor.annex,
      onSave: handleSaveAnnex,
      onCancel: () => { setActiveEditor(null); setEditingAnnex(null); },
    };

    if (activeEditor.type === "pv_preluare") return <PVPreluareGenerator {...editorProps} />;
    if (activeEditor.type === "situatie_lucrari") return <SituatieLucrariEditor {...editorProps} annexCount={annexes.filter(a => a.annex_type === "situatie_lucrari").length} />;
    if (activeEditor.type === "pv_receptie") return <PVReceptieGenerator {...editorProps} />;
    if (activeEditor.type === "conventie_ssm") return <ConventieSSMGenerator {...editorProps} />;
    if (activeEditor.type === "anexa_gdpr") return <AnexaGDPRGenerator {...editorProps} />;
  }

  const groupedAnnexes = ANNEX_TYPES.map((at) => ({
    ...at,
    items: annexes.filter((a) => a.annex_type === at.type),
  }));

  return (
    <div className="space-y-4" data-testid="contract-detail-panel">
      {/* Header */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={onBack} className="p-1 h-auto" data-testid="back-from-contract">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
                <FileText className="w-5 h-5 text-indigo-600" />
                {contract.title || "Contract Prestări Servicii"}
              </CardTitle>
              <CardDescription>Nr. {contract.registration_number} — Detalii contract și anexe</CardDescription>
            </div>
            {contract.html_content && (
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowContractEditor(true)}
                  className="text-teal-600 border-teal-200 hover:bg-teal-50"
                  data-testid="edit-contract-btn"
                >
                  <Edit className="w-4 h-4 mr-1" /> Editare format contract
                </Button>
                <Button variant="outline" size="sm" onClick={() => safeOpenHTMLWindow(contract.html_content)} data-testid="view-contract-html">
                  <Eye className="w-4 h-4 mr-1" /> Vizualizare
                </Button>
                <Button
                  size="sm"
                  onClick={() => setShowSendContract(true)}
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                  data-testid="send-contract-email-btn"
                >
                  <Mail className="w-4 h-4 mr-1" /> Trimite către client
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500">Beneficiar</p>
              <p className="font-semibold text-slate-800">{contractData.beneficiar_name || "—"}</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500">Obiect lucrare</p>
              <p className="font-semibold text-slate-800">{contractData.obiect_lucrare || "—"}</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500">Valoare contract</p>
              <p className="font-semibold text-slate-800">{(parseFloat(contractData.pret_total) || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500">Garanție</p>
              <p className="font-semibold text-slate-800">{contractData.garantie_luni || "—"} luni</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contract Editor - inline WYSIWYG */}
      {showContractEditor && (
        <ContractEditor
          document={contract}
          companyId={companyId}
          onClose={() => setShowContractEditor(false)}
          onSaved={() => { if (onRefresh) onRefresh(); }}
        />
      )}

      {/* Trimite contract pe email */}
      {showSendContract && (
        <SendContractDialog
          open={showSendContract}
          onOpenChange={setShowSendContract}
          contract={contract}
          devizData={devizData}
          companyData={companyData}
          contractNumber={contractData.contract_number || contract.title || ""}
          beneficiarName={contractData.beneficiar_name || ""}
          beneficiarEmailHint={contractData.beneficiar_email || ""}
          annexes={annexes.filter((a) => a.html_content)}
          onSent={fetchEmailHistory}
        />
      )}

      {/* Deviz asociat */}
      {devizData && (
        <Card className={`border-0 shadow-md border-l-4 ${syncStatus && !syncStatus.in_sync ? "border-l-amber-500" : "border-l-teal-500"}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${syncStatus && !syncStatus.in_sync ? "bg-amber-100" : "bg-teal-100"}`}>
                <Receipt className={`w-5 h-5 ${syncStatus && !syncStatus.in_sync ? "text-amber-600" : "text-teal-600"}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-semibold text-slate-800">Deviz #{devizData.numar_deviz || devizData.numarDeviz || "—"}</h4>
                  {syncStatus && !syncStatus.in_sync && (
                    <Badge className="bg-amber-100 text-amber-800 border-0 text-[10px]">
                      <AlertTriangle className="w-3 h-3 mr-1" /> Nesincronizat
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-slate-500 truncate">
                  {devizData.obiectiv || devizData.beneficiar || "—"} — {(devizData.totals?.totalCuTVA || devizData.totals?.totalGeneral || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei
                </p>
              </div>
              <div className="flex gap-2 flex-wrap">
                {onNavigateToDeviz && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onNavigateToDeviz(devizData.id)}
                    data-testid="open-deviz-btn"
                  >
                    <ExternalLink className="w-4 h-4 mr-1" /> Deschide în DevizePRO
                  </Button>
                )}
                {syncStatus && !syncStatus.in_sync && (
                  <Button
                    size="sm"
                    onClick={handleSyncDeviz}
                    disabled={syncing}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="sync-deviz-btn"
                  >
                    {syncing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                    Sincronizare deviz
                  </Button>
                )}
              </div>
            </div>
            {syncStatus && !syncStatus.in_sync && syncStatus.diffs?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-amber-200 bg-amber-50 -mx-4 -mb-4 px-4 pb-4">
                <div className="text-xs font-semibold text-amber-800 mb-1.5 flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" /> Modificări în deviz care nu sunt reflectate în contract:
                </div>
                <div className="space-y-1">
                  {syncStatus.diffs.map((d) => (
                    <div key={d.field} className="text-xs flex items-start gap-2 bg-white/60 rounded px-2 py-1">
                      <span className="font-semibold text-slate-700 min-w-[110px]">{d.label}:</span>
                      <span className="text-slate-500 line-through">{
                        typeof d.current === "number" ? `${d.current.toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei` : (d.current || "—")
                      }</span>
                      <span className="text-slate-400">→</span>
                      <span className="font-semibold text-emerald-700">{
                        typeof d.new === "number" ? `${d.new.toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei` : (d.new || "—")
                      }</span>
                    </div>
                  ))}
                </div>
                <div className="text-[10px] text-amber-700 mt-2">
                  Sincronizarea actualizează metadata contractului (beneficiar, valoare, obiectiv). Conținutul HTML rămâne editabil manual.
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Secțiuni Anexe */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : (
        groupedAnnexes.map((group) => {
          const colors = colorMap[group.color];
          const IconComp = group.icon;
          return (
            <Card key={group.type} className={`border-0 shadow-md border-l-4 ${colors.leftBorder}`} data-testid={`annex-group-${group.type}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <IconComp className={`w-5 h-5 ${colors.text}`} />
                    {group.label} — {group.title}
                  </CardTitle>
                  <Button
                    size="sm"
                    onClick={() => handleCreateAnnex(group.type)}
                    className={`${colors.btn} text-white`}
                    data-testid={`create-annex-${group.type}`}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    {group.type === "situatie_lucrari" ? `Situație Nr. ${group.items.length + 1}` : "Generează"}
                  </Button>
                </div>
                <CardDescription>{group.description}</CardDescription>
              </CardHeader>
              <CardContent>
                {group.items.length === 0 ? (
                  <p className="text-sm text-slate-400 italic py-2">Nicio anexă generată încă</p>
                ) : (
                  <div className="space-y-2">
                    {group.items.map((annex) => (
                      <div key={annex.id} className={`p-3 rounded-lg ${colors.bg} ${colors.border} border flex items-center justify-between`} data-testid={`annex-item-${annex.id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800">
                            <span className="inline-block px-2 py-0.5 mr-2 rounded text-xs font-bold bg-slate-800 text-white">
                              {annex.annex_label}
                            </span>
                            {annex.title}
                          </p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(annex.created_at).toLocaleDateString("ro-RO")}
                            </span>
                            <Badge className={`text-xs ${annex.status === "draft" ? "bg-slate-200 text-slate-600" : annex.status === "semnat" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                              {annex.status === "draft" ? "Ciornă" : annex.status === "semnat" ? "Semnat" : annex.status}
                            </Badge>
                            {annex.annex_type === "situatie_lucrari" && !annex.invoice_id && (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => handleGenerateInvoiceFromSL(annex)}
                                  className="h-5 px-2 text-xs bg-green-600 hover:bg-green-700 text-white"
                                  data-testid={`invoice-from-sl-${annex.id}`}
                                >
                                  <ReceiptText className="w-3 h-3 mr-1" />
                                  Generare Factură din SL
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openUploadSLDialog(annex)}
                                  className="h-5 px-2 text-xs border-teal-400 text-teal-700 hover:bg-teal-50"
                                  data-testid={`upload-invoice-sl-${annex.id}`}
                                >
                                  <Upload className="w-3 h-3 mr-1" />
                                  Încarcă factură
                                </Button>
                              </>
                            )}
                            {annex.annex_type === "situatie_lucrari" && annex.invoice_id && (() => {
                              const inv = invoiceCache[annex.invoice_id];
                              const isExternal = inv?.source === "external";
                              const invLabel = inv
                                ? (isExternal ? (inv.invoice_number || "PDF") : `${inv.series || ""}${inv.number || ""}`)
                                : "Factură";
                              return (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      if (inv) {
                                        setShowInvoiceDetail(inv);
                                      } else {
                                        // Fetch on click if not cached
                                        axios.get(`${API}/invoices/${annex.invoice_id}?company_id=${companyId}`)
                                          .then(r => {
                                            setInvoiceCache(prev => ({ ...prev, [annex.invoice_id]: r.data }));
                                            setShowInvoiceDetail(r.data);
                                          })
                                          .catch(() => toast.error("Nu s-a putut încărca factura"));
                                      }
                                    }}
                                    className={`h-5 px-2 text-xs ${isExternal ? "border-teal-300 text-teal-700 hover:bg-teal-50" : "border-green-300 text-green-700 hover:bg-green-50"}`}
                                    data-testid={`view-invoice-sl-${annex.id}`}
                                  >
                                    <ReceiptText className="w-3 h-3 mr-1" />
                                    {isExternal ? "Factură externă" : "Factură"} {invLabel}
                                  </Button>
                                  {isExternal && (
                                    <a
                                      href={`${API}/invoices/${annex.invoice_id}/external-pdf?company_id=${companyId}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center h-5 px-2 text-xs rounded border border-teal-300 text-teal-700 hover:bg-teal-50"
                                      data-testid={`download-invoice-sl-pdf-${annex.id}`}
                                    >
                                      <Download className="w-3 h-3 mr-1" /> PDF
                                    </a>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </div>
                        <div className="flex gap-1.5">
                          {annex.html_content && (
                            <>
                            <Button variant="ghost" size="sm" onClick={() => viewAnnexHTML(annex)} className="text-blue-600" title="Vizualizează" data-testid={`view-annex-${annex.id}`}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => printAnnex(annex)} className="text-slate-600" title="Printează" data-testid={`print-annex-${annex.id}`}>
                              <Printer className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => downloadAnnexPDF(annex)} className="text-teal-600" title="Descarcă PDF" data-testid={`download-annex-${annex.id}`}>
                              <Download className="w-4 h-4" />
                            </Button>
                            </>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => handleEditAnnex(annex)} className="text-indigo-600" data-testid={`edit-annex-${annex.id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteAnnex(annex.id)} className="text-red-500" data-testid={`delete-annex-${annex.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })
      )}

      {/* Istoric trimiteri pe email */}
      <Card className="border-0 shadow-md border-l-4 border-l-teal-500" data-testid="email-history-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Mail className="w-5 h-5 text-teal-600" />
            Istoric trimiteri pe email
            {emailHistory.length > 0 && (
              <Badge className="bg-teal-100 text-teal-700">{emailHistory.length}</Badge>
            )}
          </CardTitle>
          <CardDescription>Toate emailurile trimise cu acest contract</CardDescription>
        </CardHeader>
        <CardContent>
          {emailHistory.length === 0 ? (
            <p className="text-sm text-slate-400 italic py-1" data-testid="email-history-empty">
              Contractul nu a fost trimis încă pe email.
            </p>
          ) : (
            <div className="space-y-2">
              {emailHistory.map((log, i) => (
                <div
                  key={log.id}
                  className="p-3 rounded-lg bg-slate-50 border border-slate-200"
                  data-testid={`email-history-item-${i}`}
                >
                  <div className="flex items-center justify-between flex-wrap gap-1">
                    <span className="font-semibold text-sm text-slate-800">
                      {log.recipient_name || log.recipient_email}
                    </span>
                    <span className="text-xs text-slate-500">
                      {new Date(log.sent_at).toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" })}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {log.recipient_email} · {log.subject}
                  </p>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    <Badge variant="outline" className="text-xs border-teal-300 text-teal-700">
                      Contract PDF
                    </Badge>
                    {log.include_deviz && (
                      <Badge variant="outline" className="text-xs border-emerald-300 text-emerald-700">
                        Deviz
                      </Badge>
                    )}
                    {log.annex_filenames?.length > 0 && (
                      <Badge variant="outline" className="text-xs border-blue-300 text-blue-700">
                        {log.annex_filenames.length} anex{log.annex_filenames.length === 1 ? "ă" : "e"}
                      </Badge>
                    )}
                    {log.extra_filenames?.length > 0 && (
                      <Badge variant="outline" className="text-xs border-amber-300 text-amber-700">
                        {log.extra_filenames.length} fișier{log.extra_filenames.length === 1 ? "" : "e"} extra
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invoice Detail Dialog */}
      <Dialog open={!!showInvoiceDetail} onOpenChange={(open) => !open && setShowInvoiceDetail(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ReceiptText className="w-5 h-5 text-green-600" />
              Factură {showInvoiceDetail?.series}{showInvoiceDetail?.number}
            </DialogTitle>
          </DialogHeader>
          {showInvoiceDetail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">Serie / Număr</p>
                  <p className="font-bold text-slate-800">{showInvoiceDetail.series}{showInvoiceDetail.number}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">Data facturii</p>
                  <p className="font-semibold text-slate-800">{showInvoiceDetail.invoice_date || "—"}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">Client</p>
                  <p className="font-semibold text-slate-800">{showInvoiceDetail.client_name || "—"}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">Status</p>
                  <Badge className={`text-xs ${showInvoiceDetail.status === "platita" ? "bg-green-100 text-green-700" : showInvoiceDetail.status === "emisa" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-700"}`}>
                    {showInvoiceDetail.status === "platita" ? "Plătită" : showInvoiceDetail.status === "emisa" ? "Emisă" : "Ciornă"}
                  </Badge>
                </div>
              </div>
              {/* Items */}
              {showInvoiceDetail.items?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 mb-2 uppercase">Articole</p>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead><tr className="bg-slate-50 text-slate-500">
                        <th className="text-left p-2">Denumire</th><th className="text-right p-2">Cant.</th>
                        <th className="text-right p-2">Preț unit.</th><th className="text-right p-2">Total</th>
                      </tr></thead>
                      <tbody>
                        {showInvoiceDetail.items.map((item, i) => (
                          <tr key={`${item.name}-${i}`} className="border-t">
                            <td className="p-2 text-slate-700">{item.name}</td>
                            <td className="p-2 text-right text-slate-600">{item.quantity} {item.um}</td>
                            <td className="p-2 text-right text-slate-600">{(item.unit_price || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td className="p-2 text-right font-medium text-slate-700">{(item.total || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {/* Totals */}
              <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-lg p-4">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Subtotal:</span>
                  <span className="font-semibold">{(showInvoiceDetail.subtotal || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-slate-600">TVA:</span>
                  <span className="font-semibold">{(showInvoiceDetail.vat_amount || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei</span>
                </div>
                <div className="flex justify-between text-base mt-2 pt-2 border-t border-green-200">
                  <span className="font-bold text-slate-800">TOTAL:</span>
                  <span className="font-bold text-green-700">{(showInvoiceDetail.total || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei</span>
                </div>
              </div>
              {showInvoiceDetail.notes && (
                <p className="text-xs text-slate-500 italic">{showInvoiceDetail.notes}</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Upload External Invoice for SL */}
      <Dialog open={!!uploadSLDialog} onOpenChange={(open) => !open && !uploadSLSubmitting && setUploadSLDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="upload-sl-invoice-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-teal-600" />
              Încarcă factură pentru {uploadSLDialog?.annex?.annex_label || "SL"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              Selectează PDF-ul facturii emise cu alt program. Vom încerca să extragem automat
              numărul, data, suma totală și CUI-ul emitentului. Poți corecta orice câmp înainte de salvare.
            </p>
            <div>
              <Label htmlFor="upload-sl-file">Fișier PDF *</Label>
              <Input
                id="upload-sl-file"
                type="file"
                accept="application/pdf"
                onChange={(e) => handlePickPdfForSL(e.target.files?.[0])}
                disabled={uploadSLParsing || uploadSLSubmitting}
                data-testid="upload-sl-file-input"
              />
              {uploadSLParsing && (
                <p className="text-xs text-teal-600 mt-1 flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" /> Se extrag datele din PDF...
                </p>
              )}
              {uploadSLForm.file && !uploadSLParsing && (
                <p className="text-xs text-slate-500 mt-1 truncate">
                  <FileText className="inline w-3 h-3 mr-1" />{uploadSLForm.file.name}
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="upload-sl-number">Număr factură *</Label>
                <Input
                  id="upload-sl-number"
                  value={uploadSLForm.invoice_number}
                  onChange={(e) => setUploadSLForm((f) => ({ ...f, invoice_number: e.target.value }))}
                  placeholder="ex: FCT-2026-001"
                  data-testid="upload-sl-number-input"
                />
              </div>
              <div>
                <Label htmlFor="upload-sl-date">Data facturii *</Label>
                <Input
                  id="upload-sl-date"
                  type="date"
                  value={uploadSLForm.invoice_date}
                  onChange={(e) => setUploadSLForm((f) => ({ ...f, invoice_date: e.target.value }))}
                  data-testid="upload-sl-date-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="upload-sl-amount">Sumă totală (lei) *</Label>
                <Input
                  id="upload-sl-amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={uploadSLForm.total_amount}
                  onChange={(e) => setUploadSLForm((f) => ({ ...f, total_amount: e.target.value }))}
                  placeholder="0.00"
                  data-testid="upload-sl-amount-input"
                />
              </div>
              <div>
                <Label htmlFor="upload-sl-cui">CUI emitent</Label>
                <Input
                  id="upload-sl-cui"
                  value={uploadSLForm.supplier_cui}
                  onChange={(e) => setUploadSLForm((f) => ({ ...f, supplier_cui: e.target.value }))}
                  placeholder="ex: RO12345678"
                  data-testid="upload-sl-cui-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="upload-sl-notes">Note (opțional)</Label>
              <Textarea
                id="upload-sl-notes"
                rows={2}
                value={uploadSLForm.notes}
                onChange={(e) => setUploadSLForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Observații privind factura..."
                data-testid="upload-sl-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setUploadSLDialog(null)}
              disabled={uploadSLSubmitting}
              data-testid="upload-sl-cancel-btn"
            >
              Anulează
            </Button>
            <Button
              onClick={handleSubmitUploadSL}
              disabled={uploadSLSubmitting || uploadSLParsing || !uploadSLForm.file}
              className="bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="upload-sl-submit-btn"
            >
              {uploadSLSubmitting ? (
                <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Se încarcă...</>
              ) : (
                <><Upload className="w-4 h-4 mr-1" /> Încarcă și leagă de SL</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

